import React, { useState, useEffect, useRef } from 'react';
import { WebsiteDesignProps } from './types';
import { 
  Cpu, Code, Globe, Terminal, ArrowRight, ArrowUpRight, 
  Menu, X, Zap, Heart, Users, Trophy, School, Clock, 
  MapPin, Phone, Mail, GraduationCap, ChevronLeft, 
  ChevronRight, ChevronDown, Download, Send, Plus, Layout, Sparkles, 
  FileText, Instagram, Facebook, Linkedin, Youtube, Quote, BookOpen, HardDrive, Database, Shield, Camera
} from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { Button } from "@/components/ui/button";
import { VisualImageEditable } from "@/components/VisualImageEditable";
import { VisualTextEditable } from "@/components/VisualTextEditable";

const TechFuture: React.FC<WebsiteDesignProps> = ({ previewData, isEditMode, onUpdateField }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [showLoginDropdown, setShowLoginDropdown] = useState(false);
  const [activeTab, setActiveTab] = useState("all");
  const containerRef = useRef<HTMLDivElement>(null);

  // Icon resolver helper
  const getIcon = (name: string | any) => {
      if (!name) return Cpu;
      if (typeof name !== 'string') return name;
      // @ts-ignore
      const Icon = LucideIcons[name];
      return Icon || Cpu;
  };

  const schoolName = previewData?.name || "Nexus Tech Academy";

  // Shared Data Model (Strict Matching Design 1)
  const defaultData = {
    hero: {
      images: ["https://images.unsplash.com/photo-1555949963-ff9fe0c870eb?q=80&w=2070&auto=format&fit=crop"],
      tagline: ":: SYSTEM.INIT_EDUCATION_v4.0",
      titlePrefix: "Coding The",
      subtitle: "Advanced curriculum in AI, Robotics, and Software Engineering for students aged 10-18. We build the architects of the digital tomorrow."
    },
    features: [
      { icon: "Code", title: "Algorithm Design", desc: "Logic & problem solving at the core of every solution." },
      { icon: "Cpu", title: "Hardware Labs", desc: "Microcontrollers, IoT, and high-performance computing." },
      { icon: "Shield", title: "Cyber-Security", desc: "Protecting the boundaries of the digital ecosystem." }
    ],
    programs: [
      { title: "Junior Devs", age: "Ages 10-13", desc: "Foundational logic using Python and robotics kits.", img: "https://images.unsplash.com/photo-1517694712202-14dd9538aa97?q=80&w=2070&auto=format&fit=crop" },
      { title: "Pro Engenieros", age: "Ages 14-18", desc: "Full-stack development, ML models, and competitive robotics.", img: "https://images.unsplash.com/photo-1581092160562-40aa08e78837?q=80&w=2070&auto=format&fit=crop" }
    ],
    stats: [
        { value: "98%", label: "System Uptime" },
        { value: "45MB", label: "Logic Bandwidth" },
        { value: "2k+", label: "Active Nodes" }
    ],
    news: [
        { title: "Hackathon 2026", date: "MAR 10", desc: "48 hours of pure creation. Prizes totaling $10,000 in cloud credits.", img: "https://images.unsplash.com/photo-1504384308090-c894fdcc538d?q=80&w=2070&auto=format&fit=crop" }
    ],
    nav: [
        { label: 'Protocols', href: '#features' },
        { label: 'Modules', href: '#programs' },
        { label: 'Network', href: '#gallery' },
        { label: 'Support', href: '#contact' }
    ],
    team: [
        { name: "Dr. K. Aras", role: "Chief Architect", img: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?q=80&w=2070&auto=format&fit=crop" },
        { name: "Sarah Chen", role: "Logic Lead", img: "https://images.unsplash.com/photo-1580489944761-15a19d654956?q=80&w=1961&auto=format&fit=crop" }
    ],
    gallery: [
        { category: "Hardware", img: "https://images.unsplash.com/photo-1518770660439-4636190af475?q=80&w=2070&auto=format&fit=crop", title: "Chipset Alpha" },
        { category: "Software", img: "https://images.unsplash.com/photo-1555066931-4365d14bab8c?q=80&w=2070&auto=format&fit=crop", title: "Logic Flow" }
    ],
    facilities: [
        { title: "Server Room", desc: "High-density compute clusters.", img: "https://images.unsplash.com/photo-1558494949-ef010cbdcc48?q=80&w=2074&auto=format&fit=crop", icon: "Database" },
        { title: "PCB Forge", desc: "Custom hardware prototyping lab.", img: "https://images.unsplash.com/photo-1592659762303-90081d34b277?q=80&w=2045&auto=format&fit=crop", icon: "Cpu" }
    ]
  };

  const mockData = {
      ...previewData,
      hero: {
          titlePrefix: previewData?.hero?.titlePrefix || defaultData.hero.titlePrefix,
          tagline: previewData?.hero?.tagline || defaultData.hero.tagline,
          subtitle: previewData?.hero?.subtitle || defaultData.hero.subtitle,
          images: previewData?.hero?.images || defaultData.hero.images,
          backgroundImage: previewData?.hero?.backgroundImage || defaultData.hero.images[0]
      },
      about: {
          title: previewData?.about?.title || "01 // THE MANIFESTO",
          description: previewData?.about?.description || "In a world of noise, we teach the signal. Our methodology is rooted in the first principles of computation and the boundless potential of silicon-based innovation.",
          sectionImage: previewData?.about?.sectionImage || "https://images.unsplash.com/photo-1550751827-4bd374c3f58b?q=80&w=2070&auto=format&fit=crop"
      },
      features: (previewData?.features || defaultData.features).map((f: any) => ({ 
        ...f, 
        icon: getIcon(f.icon),
        desc: f.description || f.desc || ""
      })),
      programs: (previewData?.programs || defaultData.programs).map((p: any) => ({
        title: p.name || p.title || "",
        age: p.age || "",
        desc: p.description || p.desc || "",
        img: p.image || p.img || ""
      })),
      stats: (previewData?.stats || defaultData.stats).map((s: any) => ({
          value: s.value || "0",
          label: s.label || ""
      })),
      news: (previewData?.news || defaultData.news).map((n: any) => ({
        title: n.title || "",
        date: n.date || "",
        desc: n.description || n.desc || "",
        img: n.image || n.img || ""
      })),
      team: (previewData?.team || defaultData.team).map((t: any) => ({
        name: t.name || "",
        role: t.role || "",
        img: t.img || t.image || "",
        linkedin: t.linkedin || "",
        email: t.email || ""
      })),
      gallery: (previewData?.gallery || defaultData.gallery).map((g: any) => ({
        category: g.category || "General",
        title: g.caption || g.title || "",
        img: g.image || g.img || ""
      })),
      facilities: (previewData?.facilities || defaultData.facilities).map((f: any) => ({ 
        ...f, 
        icon: getIcon(f.icon),
        desc: f.description || f.desc || "",
        img: f.image || f.img || ""
      })),
      downloads: (previewData?.downloads || [
          { title: "Protocol_Handover.pkg", size: "4.2 MB", type: "PDF" },
          { title: "System_Requirements.log", size: "0.1 MB", type: "LOG" }
      ]).map((d: any) => ({
          title: d.title || "",
          size: d.size || "",
          type: d.type || "PDF"
      })),
      contact: {
        address: previewData?.contact?.address || "Port 8080, Digital District, Nairobi",
        phone: previewData?.contact?.phone || "+254 799 000 111",
        email: previewData?.contact?.email || "sysadmin@nexus.tech",
        officeHours: previewData?.contact?.officeHours || "24/7 Monitoring // Mon-Fri Visitations",
        facebook: previewData?.contact?.facebook || "",
        instagram: previewData?.contact?.instagram || "",
        youtube: previewData?.contact?.youtube || "",
        linkedin: previewData?.contact?.linkedin || ""
      },
      sectionVisibility: {
          hero: previewData?.sectionVisibility?.hero !== false,
          about: previewData?.sectionVisibility?.about !== false,
          stats: previewData?.sectionVisibility?.stats !== false,
          features: previewData?.sectionVisibility?.features !== false,
          programs: previewData?.sectionVisibility?.programs !== false,
          facilities: previewData?.sectionVisibility?.facilities !== false,
          team: previewData?.sectionVisibility?.team !== false,
          gallery: previewData?.sectionVisibility?.gallery !== false,
          news: previewData?.sectionVisibility?.news !== false,
          downloads: previewData?.sectionVisibility?.downloads !== false,
          cta: previewData?.sectionVisibility?.cta !== false,
          contact: previewData?.sectionVisibility?.contact !== false
      },
      nav: (previewData?.nav || defaultData.nav).map((n: any) => ({
          label: n.label || "",
          href: n.href || "#",
          visible: n.visible !== false
      })),
      labels: {
          downloadsSection: previewData?.labels?.downloadsSection || "System Resources",
          downloadsTitle: previewData?.labels?.downloadsTitle || "Data Download Center",
          contactFormTitle: previewData?.labels?.contactFormTitle || "Initialize Communication",
          contactFormSubtitle: previewData?.labels?.contactFormSubtitle || "Transmit your inquiry to our central processing unit for immediate routing."
      }
  };

  useEffect(() => {
    const doc = containerRef.current?.ownerDocument || document;
    const root = doc.documentElement;
    if (previewData?.theme) {
        const { colors, fonts } = previewData.theme;
        root.style.setProperty('--color-primary', colors.primary || '#06b6d4');
        root.style.setProperty('--color-secondary', colors.secondary || '#3b82f6');
        root.style.setProperty('--color-accent', colors.accent || '#8b5cf6');
        root.style.setProperty('--color-background', colors.background || '#000000');
        root.style.setProperty('--color-text', colors.text || '#00ffff');
        root.style.setProperty('--font-heading', `'${fonts.heading || 'Space Grotesk'}', sans-serif`);
        root.style.setProperty('--font-body', `'${fonts.body || 'JetBrains Mono'}', monospace`);
        const radiusMap = { none: '0px', small: '2px', medium: '4px', large: '8px', full: '9999px' };
        root.style.setProperty('--radius', radiusMap[previewData.theme.borderRadius || 'none']);
    }
    const fontLink = doc.createElement('link');
    fontLink.href = "https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@300;400;500;600;700&family=JetBrains+Mono:wght@100;300;400;500;700&display=swap";
    fontLink.rel = 'stylesheet';
    doc.head.appendChild(fontLink);
  }, [previewData?.theme]);

  const handleUpdate = (path: string, val: any) => onUpdateField && onUpdateField(path, val);
  const addArrayItem = (path: string, newItem: any) => {
    if (onUpdateField) {
        const arr = [...((mockData as any)[path] || [])];
        onUpdateField(path, [...arr, newItem]);
    }
  };
  const removeArrayItem = (path: string, index: number) => {
    if (onUpdateField && window.confirm('Defragment item?')) {
        const arr = [...((mockData as any)[path] || [])];
        onUpdateField(path, arr.filter((_, i) => i !== index));
    }
  };

  return (
    <div ref={containerRef} className="tech-future bg-[var(--color-background)] text-[var(--color-text)] min-h-screen font-[var(--font-body)] selection:bg-[var(--color-primary)] selection:text-black overflow-x-hidden">
      
      {/* HUD-Style Navigation */}
      <nav className="fixed top-0 w-full z-50 p-4 lg:p-6 bg-black/80 backdrop-blur-xl border-b border-[var(--color-primary)]/30">
          <div className="container mx-auto flex justify-between items-center">
              <div className="flex items-center gap-4">
                   <VisualImageEditable 
                      src={previewData?.logoUrl || ""} 
                      alt="Logo" 
                      containerClassName="w-10 h-10 border border-[var(--color-primary)] p-1 bg-black"
                      className="w-full h-full object-contain filter invert opacity-80"
                      onImageChange={(u) => handleUpdate('logoUrl', u)}
                      isEditMode={!!isEditMode}
                      fallback={<Terminal size={20} className="text-[var(--color-primary)]" />}
                  />
                  <div className="font-bold text-xl tracking-tighter text-[var(--color-primary)] flex items-center gap-1">
                      <span className="opacity-50 text-sm font-mono">&lt;</span>
                      <VisualTextEditable value={schoolName} onSave={(v) => handleUpdate('name', v)} isEditMode={!!isEditMode} as="span" />
                      <span className="opacity-50 text-sm font-mono"> /&gt;</span>
                  </div>
              </div>
              
              <div className="hidden lg:flex items-center gap-10">
                  <div className="flex gap-8 text-[11px] font-medium tracking-widest uppercase">
                      {mockData.nav.filter(n => n.visible).map((n, i) => (
                          <a key={i} href={n.href} className="hover:text-[var(--color-primary)] transition-all flex items-center gap-2">
                              <span className="text-[var(--color-primary)] opacity-40">0{i+1}.</span>
                              {n.label}
                          </a>
                      ))}
                  </div>
                  <div className="relative">
                      <button onClick={() => setShowLoginDropdown(!showLoginDropdown)} className="border border-[var(--color-primary)] px-6 py-2 text-[10px] uppercase font-bold tracking-widest hover:bg-[var(--color-primary)] hover:text-black transition-all flex items-center gap-2">
                          SYS_AUTH <ChevronDown size={14} className={showLoginDropdown ? 'rotate-180' : ''} />
                      </button>
                      {showLoginDropdown && (
                          <div className="absolute top-full right-0 mt-2 w-48 bg-black border border-[var(--color-primary)] p-1 z-50">
                              <a href="https://sonoma.school/guardian-login" className="block p-3 text-[10px] font-bold uppercase tracking-widest hover:bg-[var(--color-primary)] hover:text-black transition-all border-b border-[var(--color-primary)]/20">
                                  Gate_Parent
                              </a>
                              <a href="https://sonoma.school/student-login" className="block p-3 text-[10px] font-bold uppercase tracking-widest hover:bg-[var(--color-primary)] hover:text-black transition-all">
                                  Gate_Student
                              </a>
                          </div>
                      )}
                  </div>
              </div>

              <button className="lg:hidden text-[var(--color-primary)]" onClick={() => setMenuOpen(!menuOpen)}>
                  {menuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
          </div>
      </nav>

      {/* Hero Section - Glitch & Grid */}
      {mockData.sectionVisibility.hero && (
      <section className="relative min-h-screen flex flex-col justify-center px-6 pt-32 pb-20 overflow-hidden">
          {/* Static Grid Lines */}
          <div className="absolute inset-0 opacity-[0.1] pointer-events-none" 
               style={{ backgroundImage: 'linear-gradient(var(--color-primary) 1px, transparent 1px), linear-gradient(90deg, var(--color-primary) 1px, transparent 1px)', backgroundSize: '50px 50px' }} />
          
          <div className="container mx-auto grid grid-cols-1 lg:grid-cols-12 gap-16 relative z-10 items-center">
              <div className="lg:col-span-7">
                  <div className="text-[12px] font-mono text-[var(--color-secondary)] mb-6 flex items-center gap-4">
                      <span className="w-12 h-[1px] bg-[var(--color-secondary)] opacity-30" />
                      <VisualTextEditable value={mockData.hero.tagline} onSave={(v) => handleUpdate('hero.tagline', v)} isEditMode={!!isEditMode} />
                  </div>
                  <h1 className="text-6xl md:text-9xl font-bold font-[var(--font-heading)] leading-[0.9] tracking-tighter mb-10 text-white">
                      <VisualTextEditable value={mockData.hero.titlePrefix} onSave={(v) => handleUpdate('hero.titlePrefix', v)} isEditMode={!!isEditMode} /><br/>
                      <span className="text-[var(--color-primary)] drop-shadow-[0_0_15px_var(--color-primary)] italic">
                          <VisualTextEditable value={schoolName} onSave={(v) => handleUpdate('name', v)} isEditMode={!!isEditMode} />
                      </span>
                  </h1>
                      <p className="max-w-xl text-lg md:text-xl text-gray-300 mb-12 font-light leading-relaxed border-l-2 border-[var(--color-primary)]/30 pl-8">
                      <VisualTextEditable value={mockData.hero.subtitle} onSave={(v) => handleUpdate('hero.subtitle', v)} isEditMode={!!isEditMode} multiline />
                  </p>
                  <div className="flex flex-wrap gap-6">
                      <Button className="bg-[var(--color-primary)] text-black rounded-none px-12 h-16 text-[11px] font-bold uppercase tracking-[0.2em] hover:bg-white hover:shadow-[0_0_30px_var(--color-primary)] transition-all group">
                          INIT_PROGRAM <ArrowRight size={18} className="ml-4 group-hover:translate-x-2 transition-transform" />
                      </Button>
                      <button className="border border-white/20 py-4 px-10 text-[11px] font-bold uppercase tracking-widest text-white/50 hover:text-white hover:border-white transition-all">
                          QUERY_MANIFESTO
                      </button>
                  </div>
              </div>
              
              <div className="lg:col-span-5 relative">
                  <div className="relative aspect-square border border-[var(--color-primary)]/40 p-4">
                      <div className="absolute top-0 right-0 w-8 h-8 border-t-4 border-r-4 border-[var(--color-primary)]" />
                      <div className="absolute bottom-0 left-0 w-8 h-8 border-b-4 border-l-4 border-[var(--color-primary)]" />
                      <VisualImageEditable 
                          src={mockData.hero.backgroundImage} 
                          className="w-full h-full object-cover grayscale opacity-40 group-hover:opacity-100 transition-all duration-1000" 
                          alt="Hero"
                          onImageChange={(u) => handleUpdate('hero.backgroundImage', u)}
                          isEditMode={!!isEditMode}
                      />
                      {/* Floating HUD */}
                      <div className="absolute -top-6 -right-6 bg-black border border-[var(--color-primary)] p-6 shadow-2xl animate-pulse">
                          <div className="text-[10px] font-mono opacity-50 mb-2">AUTH_STATUS</div>
                          <div className="text-sm font-bold text-[var(--color-primary)]">UPTIME: 100%</div>
                      </div>
                  </div>
              </div>
          </div>
      </section>
      )}

      {/* Stats - Binary strip */}
      {mockData.sectionVisibility.stats && (
      <section className="bg-black py-20 border-y border-[var(--color-primary)]/20 relative overflow-hidden">
          <div className="container mx-auto grid grid-cols-1 md:grid-cols-3 gap-12 text-center relative z-10">
              {mockData.stats.map((stat, i) => (
                  <div key={i} className="group px-8 relative overflow-hidden">
                      <div className="text-6xl md:text-8xl font-black font-[var(--font-heading)] text-white/10 group-hover:text-[var(--color-primary)] transition-colors mb-2">
                        <VisualTextEditable value={stat.value} onSave={(v) => handleUpdate(`stats[${i}].value`, v)} isEditMode={!!isEditMode} />
                      </div>
                      <div className="text-[11px] font-bold uppercase tracking-[0.4em] text-zinc-400">
                        <VisualTextEditable value={stat.label} onSave={(v) => handleUpdate(`stats[${i}].label`, v)} isEditMode={!!isEditMode} />
                      </div>
                  </div>
              ))}
          </div>
      </section>
      )}

      {/* Features - Protocol Blocks */}
      {mockData.sectionVisibility.features && (
      <section id="features" className="py-40 px-6 container mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-24 items-center">
              <div className="lg:col-span-4">
                  <span className="text-[10px] font-mono text-[var(--color-primary)] uppercase tracking-[0.5em] mb-6 block">01 // CORE_MODULES</span>
                  <h2 className="text-6xl font-bold uppercase tracking-tight leading-none mb-10 text-white italic">The Neural Framework.</h2>
                  <p className="text-zinc-400 leading-relaxed font-light mb-12">Every student is an architect. We provide the substrate upon which they build the future of intelligence.</p>
                  <Button variant="outline" className="border-white/20 text-white hover:bg-white hover:text-black rounded-none h-14 px-8 font-black uppercase tracking-widest">Full Protocol</Button>
              </div>
              <div className="lg:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-8">
                  {mockData.features.map((item, i) => (
                      <div key={i} className="group p-12 bg-white/5 border border-white/10 hover:border-[var(--color-primary)] transition-all hover:bg-black relative overflow-hidden">
                          <div className="absolute top-0 right-0 p-6 text-[4rem] font-black italic text-white/5 opacity-5 group-hover:opacity-20 translate-x-4 -translate-y-4">0{i+1}</div>
                          <div className="w-14 h-14 bg-[var(--color-primary)]/10 text-[var(--color-primary)] flex items-center justify-center mb-10 border border-[var(--color-primary)]/30 group-hover:bg-[var(--color-primary)] group-hover:text-black transition-all">
                              {React.createElement(item.icon, { size: 28 })}
                          </div>
                          <h3 className="text-2xl font-bold uppercase mb-6 tracking-tight text-white italic">
                              <VisualTextEditable value={item.title} onSave={(v) => handleUpdate(`features[${i}].title`, v)} isEditMode={!!isEditMode} />
                          </h3>
                          <p className="text-zinc-400 leading-relaxed mb-8 group-hover:text-gray-300 transition-colors">
                              <VisualTextEditable value={item.desc} onSave={(v) => handleUpdate(`features[${i}].desc`, v)} isEditMode={!!isEditMode} multiline />
                          </p>
                          <ArrowRight className="opacity-0 group-hover:opacity-100 group-hover:translate-x-4 transition-all" size={24} />
                          {isEditMode && <button onClick={() => removeArrayItem('features', i)} className="absolute bottom-4 right-4 text-red-500 font-bold uppercase text-[9px] opacity-0 group-hover:opacity-100">DROP_NODE</button>}
                      </div>
                  ))}
                  {isEditMode && (
                      <div onClick={() => addArrayItem('features', { title: "New Module", desc: "Brief module description.", icon: "Cpu" })} className="p-12 border-2 border-dashed border-white/5 flex flex-col items-center justify-center text-gray-700 hover:text-[var(--color-primary)] hover:border-[var(--color-primary)] transition-all cursor-pointer">
                          <Plus size={48} />
                          <span className="text-[10px] font-bold uppercase tracking-widest mt-4">Inject Feature</span>
                      </div>
                  )}
              </div>
          </div>
      </section>
      )}

      {/* Programs - Module Carousel Style */}
      {mockData.sectionVisibility.programs && (
      <section id="programs" className="py-40 bg-black px-6 border-t border-white/5">
          <div className="container mx-auto">
              <div className="flex justify-between items-end mb-24 border-b border-white/10 pb-12">
                  <h2 className="text-7xl font-bold uppercase tracking-tighter text-white italic leading-none">Modules.</h2>
                  <div className="flex gap-4">
                       <button className="w-12 h-12 border border-white/10 flex items-center justify-center hover:border-[var(--color-primary)] hover:text-[var(--color-primary)] transition-all"><ChevronLeft size={20}/></button>
                       <button className="w-12 h-12 border border-white/10 flex items-center justify-center hover:border-[var(--color-primary)] hover:text-[var(--color-primary)] transition-all"><ChevronRight size={20}/></button>
                  </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                  {mockData.programs.map((prog, i) => (
                      <div key={i} className="group relative border border-white/10 hover:border-[var(--color-primary)]/50 transition-all p-8 flex flex-col md:flex-row gap-8 items-center overflow-hidden">
                          <div className="w-full md:w-2/5 aspect-[4/5] relative overflow-hidden border border-white/20">
                              <VisualImageEditable 
                                  src={prog.img} 
                                  alt={prog.title}
                                  className="w-full h-full object-cover grayscale opacity-50 group-hover:scale-110 group-hover:opacity-100 transition-all duration-1000" 
                                  onImageChange={(u) => handleUpdate(`programs[${i}].img`, u)}
                                  isEditMode={!!isEditMode}
                              />
                               <div className="absolute top-4 left-4 bg-[var(--color-primary)] text-black px-3 py-1 text-[9px] font-bold uppercase">
                                  {prog.age}
                              </div>
                          </div>
                          <div className="w-full md:w-3/5 text-center md:text-left">
                              <h3 className="text-4xl font-bold uppercase tracking-tighter mb-6 text-white italic group-hover:text-[var(--color-primary)] transition-colors leading-none">
                                  <VisualTextEditable value={prog.title} onSave={(v) => handleUpdate(`programs[${i}].title`, v)} isEditMode={!!isEditMode} />
                              </h3>
                              <p className="text-zinc-400 leading-relaxed mb-8 flex-1">
                                  <VisualTextEditable value={prog.desc} onSave={(v) => handleUpdate(`programs[${i}].desc`, v)} isEditMode={!!isEditMode} multiline />
                              </p>
                              <div className="flex items-center gap-4 text-xs font-bold uppercase tracking-widest text-white/40 group-hover:text-white transition-colors">
                                  START_SEQUENCE <ArrowRight size={16} />
                              </div>
                          </div>
                          {isEditMode && <button onClick={() => removeArrayItem('programs', i)} className="text-red-500 font-bold uppercase text-[9px] absolute top-8 right-8">NULL_PROG</button>}
                      </div>
                  ))}
                  {isEditMode && (
                      <div onClick={() => addArrayItem('programs', { title: "Next Level Lab", age: "Post-Grad", desc: "Advanced laboratory module description.", img: "" })} className="border-2 border-dashed border-white/5 aspect-video md:aspect-auto flex flex-col items-center justify-center text-gray-700 hover:text-white hover:border-white transition-all cursor-pointer">
                          <Plus size={48} className="mb-4" />
                          <span className="text-[10px] font-bold uppercase tracking-widest">Compile Module</span>
                      </div>
                  )}
              </div>
          </div>
      </section>
      )}

      {/* Team - Architect Grid */}
      {mockData.sectionVisibility.team && (
      <section id="team" className="py-40 px-6 bg-black">
          <div className="container mx-auto">
              <div className="text-center mb-32">
                   <span className="text-[10px] font-mono text-[var(--color-primary)] uppercase tracking-[0.5em] mb-6 block">CORE_TEAM_V1.2</span>
                   <h2 className="text-7xl font-bold uppercase tracking-tighter text-white italic leading-none">The Architects.</h2>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
                  {mockData.team.map((m, i) => (
                      <div key={i} className="group relative flex flex-col items-center text-center">
                          <div className="relative mb-8 overflow-hidden aspect-square w-full filter drop-shadow-[0_0_0px_var(--color-primary)] group-hover:drop-shadow-[0_0_20px_var(--color-primary)] transition-all">
                               <div className="absolute inset-0 border border-white/10 z-20 pointer-events-none group-hover:border-[var(--color-primary)] transition-colors" />
                               <VisualImageEditable 
                                  src={m.img} 
                                  alt={m.name} 
                                  className="w-full h-full object-cover grayscale opacity-60 group-hover:opacity-100 group-hover:grayscale-0 transition-all duration-700" 
                                  onImageChange={(u) => handleUpdate(`team[${i}].img`, u)} 
                                  isEditMode={!!isEditMode}
                              />
                               <div className="absolute bottom-0 right-0 p-4 bg-black border-l border-t border-[var(--color-primary)]/40 text-[var(--color-primary)] z-30">
                                   <Shield size={20} className="group-hover:animate-spin-slow transition-transform" />
                               </div>
                          </div>
                          <div>
                              <h3 className="text-3xl font-bold uppercase tracking-tight text-white italic leading-none mb-2">
                                  <VisualTextEditable value={m.name} onSave={(v) => handleUpdate(`team[${i}].name`, v)} isEditMode={!!isEditMode} />
                              </h3>
                              <p className="text-[10px] font-bold uppercase tracking-[0.4em] text-[var(--color-primary)] opacity-50 group-hover:opacity-100 transition-opacity">
                                  <VisualTextEditable value={m.role} onSave={(v) => handleUpdate(`team[${i}].role`, v)} isEditMode={!!isEditMode} />
                              </p>
                          </div>
                           {isEditMode && <button onClick={() => removeArrayItem('team', i)} className="text-red-500 font-bold uppercase text-[9px] absolute top-[-20px] mx-auto text-center w-full">EJECT_FACULTY</button>}
                      </div>
                  ))}
                  {isEditMode && (
                      <div onClick={() => addArrayItem('team', { name: "New Architect", role: "Engineer", img: "" })} className="aspect-square border-2 border-dashed border-white/5 flex flex-col items-center justify-center text-gray-700 hover:text-white transition-all cursor-pointer">
                          <Plus size={48} />
                      </div>
                  )}
              </div>
          </div>
      </section>
      )}

      {/* Gallery - Matrix View */}
      {mockData.sectionVisibility.gallery && (
      <section id="gallery" className="py-40 bg-zinc-950 px-6 border-y border-white/5">
          <div className="container mx-auto">
              <div className="flex flex-col md:flex-row justify-between items-end gap-12 mb-24 border-b border-white/10 pb-12">
                  <h2 className="text-7xl font-bold uppercase tracking-tighter text-white italic leading-none">Vaults.</h2>
                  <div className="flex gap-4 text-[10px] font-bold uppercase tracking-widest text-white/30 border-t border-white/10 py-4 w-full md:w-auto overflow-x-auto">
                      {["all", "hardware", "software", "robotics"].map(t => (
                          <button key={t} onClick={() => setActiveTab(t)} className={`px-4 py-1 hover:text-[var(--color-primary)] transition-all ${activeTab === t ? 'text-[var(--color-primary)] border-b-2 border-[var(--color-primary)]' : ''}`}>
                              {t}
                          </button>
                      ))}
                  </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {mockData.gallery.filter(item => activeTab === "all" || item.category.toLowerCase() === activeTab).map((item, i) => (
                      <div key={i} className="group relative overflow-hidden border border-white/10 aspect-video">
                          <VisualImageEditable 
                              src={item.img} alt={item.title}
                              className="w-full h-full object-cover grayscale opacity-40 group-hover:opacity-100 group-hover:grayscale-0 transition-all duration-1000"
                              onImageChange={(u) => handleUpdate(`gallery[${i}].img`, u)}
                              isEditMode={!!isEditMode}
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent p-8 flex flex-col justify-end opacity-0 group-hover:opacity-100 transition-opacity">
                              <p className="text-[10px] font-mono text-[var(--color-primary)] uppercase mb-2">&gt; {item.category}</p>
                              <h4 className="text-2xl font-bold uppercase italic text-white leading-none tracking-tight">{item.title}</h4>
                          </div>
                      </div>
                  ))}
                  {isEditMode && (
                      <div onClick={() => addArrayItem('gallery', { category: "Software", title: "New Result", img: "" })} className="border-2 border-dashed border-white/5 aspect-video flex flex-col items-center justify-center text-gray-700 hover:text-white transition-all cursor-pointer">
                          <Camera size={32} className="mb-4" />
                          <span className="text-[10px] font-bold uppercase tracking-widest">Snapshot Data</span>
                      </div>
                  )}
              </div>
          </div>
      </section>
      )}

      {/* News & Updates - Data Stream */}
      {mockData.sectionVisibility.news && (
      <section className="py-40 px-6 bg-black">
          <div className="container mx-auto">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-32 border-b border-white/10 pb-12 gap-8">
                  <div>
                      <span className="text-[10px] font-mono text-[var(--color-primary)] uppercase tracking-[0.5em] mb-4 block">STREAM_MONITOR</span>
                      <h2 className="text-7xl font-bold uppercase tracking-tighter text-white italic leading-none">The Signal.</h2>
                  </div>
                  <Button variant="outline" className="h-14 border-[var(--color-primary)] text-[var(--color-primary)] hover:bg-[var(--color-primary)] hover:text-black font-bold uppercase tracking-widest rounded-none">History_Log</Button>
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
                  {mockData.news.map((item, i) => (
                      <div key={i} className="group flex flex-col border border-white/10 p-10 hover:border-[var(--color-primary)]/50 transition-all hover:bg-white/5 relative">
                          <div className="text-[10px] font-mono text-zinc-400 mb-8 flex justify-between tracking-widest">
                              <span>TIMESTAMP: {item.date}</span>
                              <span className="text-[var(--color-primary)] opacity-50">NODE_{i+1}</span>
                          </div>
                          <h3 className="text-3xl font-bold uppercase tracking-tighter leading-none mb-10 text-white italic group-hover:text-[var(--color-primary)] transition-colors">
                              <VisualTextEditable value={item.title} onSave={(v) => handleUpdate(`news[${i}].title`, v)} isEditMode={!!isEditMode} />
                          </h3>
                          <div className="h-60 overflow-hidden mb-10 relative border border-white/5">
                               <VisualImageEditable 
                                  src={item.img} alt={item.title}
                                  className="w-full h-full object-cover grayscale opacity-50 group-hover:scale-105 transition-all duration-700"
                                  onImageChange={(u) => handleUpdate(`news[${i}].img`, u)}
                                  isEditMode={!!isEditMode}
                              />
                          </div>
                          <p className="text-zinc-400 font-light leading-relaxed mb-10 line-clamp-3 group-hover:text-zinc-400/80">
                              <VisualTextEditable value={item.desc} onSave={(v) => handleUpdate(`news[${i}].desc`, v)} isEditMode={!!isEditMode} multiline />
                          </p>
                          <div className="mt-auto pt-8 border-t border-white/5 flex items-center justify-between group-hover:border-[var(--color-primary)]/20 transition-colors">
                               <div className="flex gap-2 text-[var(--color-primary)]">
                                   <div className="w-2 h-2 rounded-full bg-current animate-pulse" />
                                   <span className="text-[10px] font-bold uppercase tracking-widest">READ_PACKET</span>
                               </div>
                               <ArrowUpRight size={20} className="text-white group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                          </div>
                      </div>
                  ))}
              </div>
          </div>
      </section>
      )}

      {/* Downloads / Protocol Assets */}
      {mockData.sectionVisibility.downloads && (
      <section className="py-32 bg-zinc-900/50 border-t border-white/5 px-6">
          <div className="container mx-auto">
              <div className="flex flex-col md:flex-row justify-between items-end gap-12 mb-24">
                  <div className="max-w-xl">
                      <span className="text-[10px] font-mono text-[var(--color-primary)] uppercase tracking-[0.5em] mb-6 block">
                          <VisualTextEditable 
                              value={mockData.labels.downloadsSection} 
                              onSave={(v) => handleUpdate('labels.downloadsSection', v)} 
                              isEditMode={!!isEditMode} 
                              as="span" 
                          />
                      </span>
                      <h2 className="text-5xl md:text-7xl font-bold uppercase tracking-tighter text-white italic">
                          <VisualTextEditable 
                              value={mockData.labels.downloadsTitle} 
                              onSave={(v) => handleUpdate('labels.downloadsTitle', v)} 
                              isEditMode={!!isEditMode} 
                              as="span" 
                          />
                      </h2>
                  </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {mockData.downloads.map((doc, i) => (
                      <div key={i} className="group bg-black p-10 border border-white/5 hover:border-[var(--color-primary)]/50 transition-all flex flex-col justify-between min-h-[300px] relative overflow-hidden">
                          <div className="absolute top-0 right-0 w-32 h-32 bg-[var(--color-primary)]/5 rounded-full blur-[60px] translate-x-16 -translate-y-16 group-hover:bg-[var(--color-primary)]/10 transition-colors" />
                          <div className="flex justify-between items-start mb-12 relative z-10">
                              <div className="w-16 h-16 bg-white/5 flex items-center justify-center text-[var(--color-primary)] group-hover:scale-110 transition-transform">
                                  <Download size={28} />
                              </div>
                              <ArrowUpRight size={24} className="text-zinc-700 group-hover:text-white transition-colors" />
                          </div>
                          <div className="relative z-10">
                              <h4 className="text-2xl font-bold text-white uppercase tracking-tight mb-4">
                                  <VisualTextEditable value={doc.title} onSave={(v) => handleUpdate(`downloads[${i}].title`, v)} isEditMode={!!isEditMode} />
                              </h4>
                              <p className="text-[10px] font-mono text-zinc-500 uppercase tracking-widest italic group-hover:text-[var(--color-primary)]/60 transition-colors">
                                  OBJ_TYPE: <VisualTextEditable value={doc.type} onSave={(v) => handleUpdate(`downloads[${i}].type`, v)} isEditMode={!!isEditMode} as="span" /> // 
                                  PAYLOAD_SIZE: <VisualTextEditable value={doc.size} onSave={(v) => handleUpdate(`downloads[${i}].size`, v)} isEditMode={!!isEditMode} as="span" />
                              </p>
                          </div>
                          {isEditMode && <button onClick={() => removeArrayItem('downloads', i)} className="text-[8px] font-black uppercase text-red-500 mt-8 tracking-[0.2em] relative z-10 hover:text-red-400 transition-colors">DELETE_ASSET</button>}
                      </div>
                  ))}
                  {isEditMode && (
                      <div onClick={() => addArrayItem('downloads', { title: "NEW_PROTOCOL", size: "1.0 MB", type: "PDF" })} className="p-10 border-2 border-dashed border-white/10 flex flex-col items-center justify-center text-zinc-700 hover:text-[var(--color-primary)] hover:border-[var(--color-primary)] transition-all cursor-pointer min-h-[300px]">
                          <Plus size={48} className="mb-4" />
                          <span className="text-[10px] font-black uppercase tracking-[0.4em]">MOUNT_NEW_VOLUME</span>
                      </div>
                  )}
              </div>
          </div>
      </section>
      )}

      {/* CTA - Global Protocol */}
      {mockData.sectionVisibility.cta && (
      <section id="cta" className="py-64 relative bg-black overflow-hidden border-t border-white/5 text-center px-6">
          <div className="absolute inset-0 opacity-10" 
               style={{ backgroundImage: 'radial-gradient(circle at center, var(--color-primary) 1px, transparent 1px)', backgroundSize: '30px 30px' }} />
          <div className="container mx-auto relative z-10 max-w-4xl">
              <span className="text-[10px] font-mono text-[var(--color-primary)] uppercase tracking-[1em] mb-12 block">END_OF_TRANSMISSION</span>
              <h2 className="text-7xl md:text-9xl font-bold uppercase tracking-tighter text-white leading-none italic mb-16 shadow-[0_0_50px_rgba(6,182,212,0.1)]">
                 JOIN THE <br/> <span className="text-[var(--color-primary)]">NETWORK.</span>
              </h2>
              <p className="text-xl md:text-2xl font-light text-zinc-400 mb-20 leading-relaxed italic max-w-2xl mx-auto">
                  Synchronize with the Nexus ecosystem and start your deployment today. The next version of the world is being built here.
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-8">
                   <Button className="bg-[var(--color-primary)] text-black rounded-none h-20 px-16 font-black uppercase tracking-[0.4em] text-xs hover:shadow-[0_0_40px_var(--color-primary)] transition-all">
                       INITIATE_UPGRADE()
                   </Button>
                   <Button variant="outline" className="border-white/20 text-white rounded-none h-20 px-16 font-black uppercase tracking-[0.4em] text-xs hover:bg-white hover:text-black transition-all">
                       QUERY_PROTOCOLS
                   </Button>
              </div>
          </div>
      </section>
      )}

      {/* Footer - Binary Ground */}
      <footer id="contact" className="bg-black text-white pt-40 pb-20 px-6 border-t border-[var(--color-primary)]/40 relative">
          <div className="container mx-auto relative z-10">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-24 mb-32 items-start">
                  <div className="lg:col-span-5">
                      <div className="text-4xl font-bold uppercase tracking-tighter text-[var(--color-primary)] flex items-center gap-2 italic leading-none mb-12">
                          <span className="opacity-50 text-xl font-mono">&lt;</span>{schoolName}<span className="opacity-50 text-xl font-mono"> /&gt;</span>
                      </div>
                      <p className="text-zinc-400 font-light leading-relaxed mb-12 max-w-md italic border-l-2 border-[var(--color-primary)] pl-8">
                        "In an era governed by algorithms, the only true liberty is understanding the code. Join the signal."
                      </p>
                      <div className="flex gap-4">
                          {[Facebook, Instagram, Youtube, Linkedin].map((Icon, i) => (
                              <a key={i} href="#" className="w-12 h-12 border border-white/10 flex items-center justify-center hover:bg-[var(--color-primary)] hover:text-black transition-all">
                                <Icon size={20} />
                              </a>
                          ))}
                      </div>
                  </div>
                  
                  <div className="lg:col-span-2">
                      <h4 className="text-[10px] font-bold tracking-[0.6em] uppercase text-[var(--color-primary)]/50 mb-10">DIRECTORY.EXE</h4>
                      <ul className="space-y-6 text-[11px] font-bold uppercase tracking-widest">
                          {mockData.nav.map((item, i) => (
                              <li key={i}><a href={item.href} className="text-zinc-400 hover:text-[var(--color-primary)] transition-all flex items-center gap-4">[{i.toString().padStart(2, '0')}] {item.label}</a></li>
                          ))}
                      </ul>
                  </div>

                  <div className="lg:col-span-3">
                      <h4 className="text-[10px] font-bold tracking-[0.6em] uppercase text-[var(--color-primary)]/50 mb-10">ACCESS_COORDINATES</h4>
                      <div className="space-y-10">
                          <div className="flex gap-6 group">
                              <MapPin size={24} className="text-[var(--color-primary)] shrink-0" />
                              <p className="text-sm font-bold uppercase text-zinc-400/80 group-hover:text-white transition-colors leading-tight">
                                  <VisualTextEditable value={mockData.contact.address} onSave={(v) => handleUpdate('contact.address', v)} isEditMode={!!isEditMode} multiline />
                              </p>
                          </div>
                          <div className="flex gap-6 group">
                              <Phone size={24} className="text-[var(--color-secondary)] shrink-0" />
                              <p className="text-sm font-bold uppercase text-zinc-400/80 group-hover:text-white transition-colors">
                                  <VisualTextEditable value={mockData.contact.phone} onSave={(v) => handleUpdate('contact.phone', v)} isEditMode={!!isEditMode} />
                              </p>
                          </div>
                      </div>
                  </div>

                  <div className="lg:col-span-2">
                      <h4 className="text-[10px] font-bold tracking-[0.6em] uppercase text-[var(--color-primary)]/50 mb-10">BROADCAST_SUBSCRIPTION</h4>
                      <div className="space-y-6">
                           <p className="text-[10px] text-zinc-400 italic font-medium leading-relaxed">Join the data stream for real-time architecture updates.</p>
                           <div className="flex gap-2 border-b border-white/20 pb-3 hover:border-[var(--color-primary)] transition-all">
                                <input type="email" placeholder="USER@HOST.DAT" className="bg-transparent border-none focus:ring-0 text-[10px] font-mono tracking-widest w-full placeholder:text-gray-700" />
                                <button className="text-[var(--color-primary)]"><Send size={20} /></button>
                           </div>
                      </div>
                  </div>
              </div>
              
              <div className="pt-20 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-12 text-[10px] font-bold tracking-[0.4em] uppercase text-gray-600">
                  <p>&copy; {new Date().getFullYear()} {schoolName} // NODE_ALPHA_ACTIVE // ALL LOGS SECURE</p>
                  <div className="flex gap-12 font-mono">
                      <a href="#" className="hover:text-[var(--color-primary)]">PRIVACY_POLICY</a>
                      <a href="#" className="hover:text-[var(--color-primary)]">SYSTEM_RULES</a>
                  </div>
              </div>
          </div>
      </footer>

      {/* Mobile Menu Overlay - Full Vector Grid */}
      {menuOpen && (
          <div className="fixed inset-0 z-[100] bg-black p-8 flex flex-col justify-between animate-in fade-in slide-in-from-right duration-500 overflow-hidden">
              <div className="absolute inset-0 opacity-5 pointer-events-none" 
                   style={{ backgroundImage: 'linear-gradient(var(--color-primary) 1px, transparent 1px), linear-gradient(90deg, var(--color-primary) 1px, transparent 1px)', backgroundSize: '100px 100px' }} />
              
              <div className="flex justify-between items-center relative z-10 border-b border-[var(--color-primary)]/20 pb-6">
                  <div className="text-2xl font-bold uppercase italic text-[var(--color-primary)] leading-none">{schoolName}</div>
                  <button onClick={() => setMenuOpen(false)} className="text-[var(--color-primary)]"><X size={32} /></button>
              </div>
              <nav className="flex flex-col gap-8 relative z-10">
                  {mockData.nav.filter(n => n.visible).map((n, i) => (
                      <a key={i} href={n.href} onClick={() => setMenuOpen(false)} className="text-5xl font-bold uppercase tracking-tight italic text-gray-600 hover:text-[var(--color-primary)] transition-all flex items-baseline gap-4">
                          <span className="text-[10px] font-mono opacity-40 italic">MOD_0{i+1}</span>
                          {n.label}
                      </a>
                  ))}
              </nav>
              <div className="pt-20 border-t border-[var(--color-primary)]/20 relative z-10">
                  <Button className="w-full bg-[var(--color-primary)] text-black rounded-none h-20 text-[11px] font-black uppercase tracking-[0.5em] hover:bg-white transition-all">AUTH_CONNECTION</Button>
              </div>
          </div>
      )}
    </div>
  );
};

export default TechFuture;
